import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import Modal from "../Modal";
import { Dialog } from "@headlessui/react";
import Logo from "../../assets/Logo.png";

const CallerList = ({
    sendJsonMessage,
    userData,
    lastJsonMessage,
    selectedMedicalOffice,
    setStep,
    consultationType,
}) => {
    let didInit = false;
    const [patientQueue, setPatientQueue] = useState([]);
    const [pendingAppointments, setPendingAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [turnToSkip, setTurnToSkip] = useState(null);
    const [lastSyncTime, setLastSyncTime] = useState(new Date());

    const getPatientQueue = () => {
        sendJsonMessage(
            {
                action: "get_queue",
                data: {
                    doctor_id: userData.id,
                },
            },
            false
        );
    };

    const callPatient = (patientId) => {
        sendJsonMessage(
            {
                action: "call_patient",
                data: {
                    doctor_id: userData.id,
                    medical_office_id: selectedMedicalOffice,
                    patient_id: patientId,
                },
            },
            false
        );
    };

    const skipPatient = () => {
        sendJsonMessage(
            {
                action: "mark_missed_appointment",
                data: {
                    turn_id: turnToSkip.id,
                },
            },
            false
        );
        setOpen(false);
    };

    const removeFromQueue = (turnId) => {
        setPatientQueue(patientQueue.filter((turn) => turn.id !== turnId));
    };

    const finishTurn = (turnId) => {
        sendJsonMessage(
            {
                action: "mark_attended",
                data: { turn_id: turnId },
            },
            false
        );

        setPatientQueue(
            patientQueue.map((turn) =>
                turn.id === turnId ? { ...turn, attended: true } : turn
            )
        );
    };


    useEffect(() => {
        if (!didInit) {
            didInit = true;
            getPatientQueue();
        }

        return () => {
            sendJsonMessage(
                {
                    action: "logout",
                    data: {
                        doctor_id: userData.id,
                    },
                },
                false
            );
        };
    }, []);

    useEffect(() => {
        if (!lastJsonMessage) return;

        console.log("WebSocket mensaje recibido:", lastJsonMessage);

        if (
            lastJsonMessage.type === "no_patient" &&
            lastJsonMessage.status === "pass"
        ) {
            setStep(3);
        } else if (
            lastJsonMessage.type === "connection" &&
            lastJsonMessage.status === "success" &&
            lastJsonMessage.message?.includes("Conectado a la cola")
        ) {
            // Cuando se reconecta al grupo, la cola ya fue enviada automáticamente por el backend
            console.log("Reconectado al grupo, esperando cola actualizada...");
        } else if (
            lastJsonMessage.type === "queue" &&
            lastJsonMessage.status === "success" &&
            lastJsonMessage.message === "Nuevo turno creado."
        ) {
            console.log("Nuevo turno detectado:", lastJsonMessage.data);
            console.log("Turno appointment_id:", lastJsonMessage.data.appointment_id);
            console.log("Turno scheduled_time:", lastJsonMessage.data.scheduled_time);
            
            // Verificar que el turno no exista ya en la cola usando el estado más reciente
            setPatientQueue((prevQueue) => {
                const turnExists = prevQueue.some(
                    (turn) => turn.id === lastJsonMessage.data.id
                );
                if (turnExists) {
                    return prevQueue; // No agregar duplicado
                }
                return [
                    ...prevQueue,
                    { ...lastJsonMessage.data, ready: false, on_hold: false },
                ];
            });
            
            // Si el turno tiene una cita vinculada, removerla de pendingAppointments
            if (lastJsonMessage.data.appointment_id) {
                console.log("Removiendo appointment_id de pendientes:", lastJsonMessage.data.appointment_id);
                setPendingAppointments((prevPending) => {
                    console.log("Pending appointments antes:", prevPending);
                    const filtered = prevPending.filter(
                        (appt) => appt.appointment_id !== lastJsonMessage.data.appointment_id
                    );
                    console.log("Pending appointments después:", filtered);
                    return filtered;
                });
            } else {
                console.log("Turno SIN appointment_id - no se remueve de pendientes");
            }
        } else if (
            lastJsonMessage.type === "queue" &&
            lastJsonMessage.status === "success" &&
            lastJsonMessage.data?.patients
        ) {
            console.log(
                "Cola sincronizada, pacientes recibidos:",
                lastJsonMessage.data.patients.length
            );
            const newPatients = lastJsonMessage.data.patients.map((patient) => {
                return { ...patient, ready: false, on_hold: false };
            });
            
            setPatientQueue(newPatients);
            
            // Filtrar appointments que ya tienen turno creado
            const appointmentIdsInQueue = newPatients
                .filter(turn => turn.appointment_id)
                .map(turn => turn.appointment_id);
            
            const filteredAppointments = (lastJsonMessage.data.pending_appointments || [])
                .filter(appt => !appointmentIdsInQueue.includes(appt.appointment_id));
            
            setPendingAppointments(filteredAppointments);
            setLastSyncTime(new Date());
            setLoading(false);
        } else if (
            lastJsonMessage.type === "appointment" &&
            lastJsonMessage.status === "success"
        ) {
            // Nueva cita creada
            if (lastJsonMessage.message === "Nuevo appointment creado" && lastJsonMessage.data) {
                const newAppointment = {
                    appointment_id: lastJsonMessage.data.id,
                    patient_id: lastJsonMessage.data.patient?.id,
                    patient: lastJsonMessage.data.patient 
                        ? `${lastJsonMessage.data.patient.firstName} ${lastJsonMessage.data.patient.lastName}`
                        : null,
                    gov_id: lastJsonMessage.data.patient?.gov_id,
                    age: lastJsonMessage.data.patient?.age,
                    scheduled_time: lastJsonMessage.data.time ? lastJsonMessage.data.time.slice(0, 5) : null,
                    appointment_notes: lastJsonMessage.data.notes,
                    appointment_status: lastJsonMessage.data.status,
                };
                
                // Solo agregar si el paciente existe y no está ya en la cola
                if (newAppointment.patient) {
                    setPendingAppointments((prevPending) => {
                        const alreadyPending = prevPending.some(
                            appt => appt.appointment_id === newAppointment.appointment_id
                        );
                        if (!alreadyPending) {
                            return [...prevPending, newAppointment];
                        }
                        return prevPending;
                    });
                }
            }
            setLoading(false);
        }
    }, [lastJsonMessage]);

    const confirmation = (
        <div className="bg-white dark:bg-gray-900 text-black dark:text-white">
            <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                    <ExclamationTriangleIcon
                        className="h-6 w-6 text-yellow-800"
                        aria-hidden="true"
                    />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title
                        as="h3"
                        className="text-lg leading-6 font-medium text-black dark:text-white"
                    >
                        Confirmar
                    </Dialog.Title>
                    <div className="mt-2">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            ¿Estás seguro de que quieres marcar como ausente a
                            este paciente?
                        </p>
                    </div>
                </div>
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary sm:col-start-2 sm:text-sm"
                    onClick={() => {
                        skipPatient();
                        removeFromQueue(turnToSkip.id);
                    }}
                >
                    Aceptar
                </button>
                <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => {
                        setTurnToSkip(null);
                        setOpen(false);
                    }}
                >
                    Cancelar
                </button>
            </div>
        </div>
    );

    return (
        <div className="-my-2 sm:-mx-6 lg:-mx-8 w-full self-start mt-8">
            {loading ? (
                <div className="h-screen flex items-center justify-center">
                    <div className="w-10 h-10 mx-5 border-b-2 border-primary rounded-full animate-spin"></div>
                </div>
            ) : (
                <div>
                    {patientQueue.length > 0 ? (
                        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                            <h1 class="text-2xl font-bold leading-9 text-gray-600 dark:text-gray-400 mb-8">
                                Gestión de turnos de consultas -{" "}
                                {`${userData.gender === 2 ? "Dra" : "Dr"}. ${
                                    userData.firstName
                                } ${userData.lastName}`}
                            </h1>

                            <div
                                className={` border-gray-200 dark:border-gray-700`}
                            >
                                <Modal
                                    size="sm:max-w-lg w-full"
                                    content={confirmation}
                                    open={open}
                                    setOpen={setOpen}
                                />

                                <div className="max-w-[95dvw]">
                                    <h2 className="text-gray-600 dark:text-gray-400 text-xl font-bold mb-8">
                                        Agenda y llegadas del día
                                    </h2>
                                    <CombinedTable
                                        patientQueue={patientQueue}
                                        pendingAppointments={pendingAppointments}
                                        callPatient={callPatient}
                                        setPatientQueue={setPatientQueue}
                                        setTurnToSkip={setTurnToSkip}
                                        finishTurn={finishTurn}
                                        setOpen={setOpen}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col md:flex-row gap-40">
                            <div className="max-w-[95dvw]">
                                <h2 className="text-gray-600 dark:text-gray-400 text-xl font-bold mb-8">
                                    Mi agenda del día
                                </h2>
                                <CombinedTable
                                    patientQueue={[]}
                                    pendingAppointments={pendingAppointments}
                                    callPatient={callPatient}
                                    setPatientQueue={setPatientQueue}
                                    setTurnToSkip={setTurnToSkip}
                                    finishTurn={finishTurn}
                                    setOpen={setOpen}
                                />
                            </div>
                            <div className=" sm:w-full sm:max-w-md">
                                <img
                                    className="w-48 mx-auto"
                                    src={Logo}
                                    alt="Logo"
                                />
                                <h2 className="text-center text-[30px] font-bold leading-9 text-primary mb-10">
                                    Ya no quedan pacientes por atender
                                </h2>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
export default CallerList;

const CombinedTable = ({
    patientQueue,
    pendingAppointments,
    callPatient,
    setPatientQueue,
    setTurnToSkip,
    finishTurn,
    setOpen,
}) => {
    // Combinar turnos y citas pendientes
    const combinedRows = [
        ...patientQueue.map(turn => ({ ...turn, type: 'turn', isPending: false })),
        ...pendingAppointments.map(appt => ({ ...appt, type: 'appointment', isPending: true }))
    ];

    // Ordenar por hora de cita si existe, sino por hora de llegada
    combinedRows.sort((a, b) => {
        const timeA = a.scheduled_time || a.created_at?.split(' ')[1] || '99:99';
        const timeB = b.scheduled_time || b.created_at?.split(' ')[1] || '99:99';
        return timeA.localeCompare(timeB);
    });

    return (
        <div className="w-full overflow-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900 z-30 shadow-sm">
                    <tr>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                            Paciente
                        </th>
                        <th
                            scope="col"
                            className="py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                            Hora Cita
                        </th>
                        <th
                            scope="col"
                            className="py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                            Hora Llegada
                        </th>
                        <th
                            scope="col"
                            className=" py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                            Edad
                        </th>
                        {/* <th
                            scope="col"
                            className="py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                            CI
                        </th> */}
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                            Notas
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                            Dx
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                            Estado
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        ></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                    {combinedRows.length === 0 ? (
                        <tr>
                            <td colSpan={9} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                                No hay pacientes ni citas pendientes
                            </td>
                        </tr>
                    ) : (
                        combinedRows.map((el) => (
                        <tr
                            key={el.type === 'turn' ? `turn-${el.id}` : `appt-${el.appointment_id}`}
                            className={
                                el.attended
                                    ? "bg-[#9CEC5B] dark:bg-[#255507]"
                                    : el.on_hold
                                    ? "bg-[#ffd6a8]"
                                    : ""
                            }
                        >
                            <td className="px-6 whitespace-nowrap text-left text-sm font-medium text-primary hover:text-primary">
                                <Link
                                    to={`/patients/${el.patient_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {el.patient}
                                </Link>
                            </td>
                            <td className="whitespace-nowrap text-center text-sm font-medium">
                                <div className="text-gray-900 dark:text-white">
                                    {el.scheduled_time || "--:--"}
                                </div>
                            </td>
                            <td className="whitespace-nowrap text-center text-sm font-medium">
                                <div className="text-gray-900 dark:text-white">
                                    {el.type === 'turn' && el.created_at ? (() => {
                                        const [_, time] = el.created_at.split(" ");
                                        let [hours, minutes] = time.split(":").map(Number);
                                        hours = (hours - 3 + 24) % 24;
                                        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
                                    })() : "--:--"}
                                </div>
                            </td>
                            <td className="whitespace-nowrap text-center text-sm font-medium">
                                <div className="text-gray-900 dark:text-white">
                                    {el.age < 1 ? "RN" : el.age || "--"}
                                </div>
                            </td>
                            {/* <td className="whitespace-nowrap text-right text-sm font-medium">
                                <div className="text-gray-900 dark:text-white">
                                    {el.gov_id || "--"}
                                </div>
                            </td> */}
                            <NotesCell notes={el.appointment_notes || (el.is_urgent ? "Urgente" : null)} />
                            <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-200 max-w-xs">
                                {el.last_diagnosis || "--"}
                            </td>
                            <td className="px-6 whitespace-nowrap text-center text-sm font-medium">
                                {el.isPending ? (
                                    <span className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/10">
                                        Pendiente
                                    </span>
                                ) : el.attended ? (
                                    <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/10">
                                        Atendido
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/10">
                                        Llegó
                                    </span>
                                )}
                            </td>

                            <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                                {el.type === 'turn' && !el.attended ? (
                                    <div className="flex gap-x-2 justify-center">
                                        <button
                                            onClick={() =>
                                                callPatient(el.patient_id)
                                            }
                                            className="w-1/3  flex justify-center py-2 px-4 border border-[#75C9EE] rounded-md shadow-sm text-sm font-medium text-[#75C9EE] active:bg-blue-100"
                                        >
                                            Llamar
                                        </button>
                                        <button
                                            onClick={() =>
                                                setPatientQueue(
                                                    patientQueue.map(
                                                        (patient) => {
                                                            if (
                                                                patient.gov_id ===
                                                                el.gov_id
                                                            ) {
                                                                return {
                                                                    ...patient,
                                                                    on_hold: true,
                                                                };
                                                            }
                                                            return patient;
                                                        }
                                                    )
                                                )
                                            }
                                            className="w-1/3 flex justify-center py-2 px-4 border rounded-md shadow-sm text-sm font-medium text-amber-600 border-amber-400 active:bg-amber-100"
                                        >
                                            En espera
                                        </button>
                                        <button
                                            onClick={() => {
                                                setTurnToSkip(el);
                                                setOpen(true);
                                            }}
                                            className="w-1/3  flex justify-center py-2 px-4 border rounded-md shadow-sm text-sm font-medium text-white  bg-red active:bg-rose-400"
                                        >
                                            Ausente
                                        </button>
                                        <button
                                            onClick={() => finishTurn(el.id)}
                                            className="w-1/3  flex justify-center py-2 px-4 border rounded-md shadow-sm text-sm font-medium text-teal-600  border-teal-600 active:bg-teal-100"
                                        >
                                            Finalizar
                                        </button>
                                    </div>
                                ) : null}
                            </td>
                        </tr>
                    ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

function AppointmentsTable({ appointments }) {
    const formatTime = (timeString) => {
        return timeString ? timeString.slice(0, 5) : "--:--";
    };

    return (
        <div className=" w-full overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Paciente
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Hora
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Notas
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                    {appointments.length === 0 ? (
                        <tr>
                            <td
                                colSpan={3}
                                className="px-6 py-4 text-center text-gray-500 dark:text-gray-400"
                            >
                                No hay citas disponibles
                            </td>
                        </tr>
                    ) : (
                        appointments.map((appt) => (
                            <tr
                                key={appt.id}
                                className="hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium ">
                                    {appt.patient ? (
                                        <Link
                                            to={`/patients/${appt.patient.id}`}
                                            className="hover:underline text-primary hover:text-primary"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {appt.patient.firstName}{" "}
                                            {appt.patient.lastName}
                                        </Link>
                                    ) : (
                                        <span className="text-red-800 bg-red-100 font-semibold p-1 rounded-full">
                                            Bloqueado
                                        </span>
                                    )}
                                </td>

                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
                                    {formatTime(appt.time)}
                                </td>

                                <NotesCell notes={appt.notes} />
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}

function NotesCell({ notes }) {
    const [expanded, setExpanded] = useState(false);

    if (!notes)
        return (
            <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-200">
                --
            </td>
        );

    const cleanText = notes.replace(/<[^>]+>/g, "");
    const limit = 100;
    const shouldTruncate = cleanText.length > limit;

    return (
        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-200 max-w-xs">
            <div>
                <div
                    className={`${
                        !expanded && "line-clamp-3 overflow-hidden"
                    } `}
                    dangerouslySetInnerHTML={{
                        __html: expanded
                            ? notes
                            : cleanText.slice(0, limit) +
                              (shouldTruncate ? "..." : ""),
                    }}
                ></div>
                {shouldTruncate && (
                    <button
                        className="text-primary hover:underline mt-1 text-xs"
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? "Ver menos" : "Ver más"}
                    </button>
                )}
            </div>
        </td>
    );
}
